import React, { useMemo, useEffect } from "react";
import Grid from "@mui/material/Grid";
import { Box, Typography } from "@mui/material";
import Chart from "react-apexcharts";

import VuiBox from "components/ui/VuiBox";
import DashboardLayout from "components/layout/DashboardLayout";
import DashboardNavbar from "components/navigation/DashboardNavbar";
import PorterTable from "components/PorterTable";

// Custom components
import GlassCard from "components/GlassCard";

// Design tokens
import { ACCENT_COLORS } from "utils/designTokens";

// Mock data
import mockPricingData from "./data/mockPricingData";
import RegionalPricingMap from "./components/RegionalPricingMap";
import { analyticsService } from "services/trackingService";

// ---------------------------------------------------------------------------
// Constants & Styling
// ---------------------------------------------------------------------------
const { primary: ACCENT_OURS, secondary: ACCENT_THEIRS } = ACCENT_COLORS;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

function PricingAnalyticsPage() {
  useEffect(() => {
    analyticsService.trackPageView("PRICING_ANALYTICS", {});
  }, []);
  // Format data for Chart
  const chartData = useMemo(() => {
    const data = mockPricingData.regions;
    
    // Create a single series with each product as a separate row to avoid overlap issues
    // and ensure perfect rendering of ranges across all ApexCharts versions.
    const combinedData = [];

    data.forEach((region) => {
      combinedData.push({
        x: `${region.name} (WEB 17)`,
        y: [region.our_product.min, region.our_product.max],
        fillColor: ACCENT_OURS,
      });
      combinedData.push({
        x: `${region.name} (Artisse)`,
        y: [region.competitor_product.min, region.competitor_product.max],
        fillColor: ACCENT_THEIRS,
      });
    });

    return {
      series: [
        { name: "Price Range", data: combinedData }
      ],
      options: {
        chart: {
          type: "rangeBar",
          background: "transparent",
          toolbar: { show: false },
          fontFamily: "inherit",
        },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 4,
            barHeight: "70%",
          }
        },
        xaxis: {
          labels: {
            style: { colors: "rgba(255,255,255,0.7)", fontSize: "12px" },
            formatter: (val) => "€" + Number(val).toLocaleString()
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { colors: "rgba(255,255,255,0.8)", fontSize: "11px", fontWeight: 600 },
            maxWidth: 200,
          }
        },
        grid: {
          borderColor: "rgba(255,255,255,0.1)",
          xaxis: { lines: { show: true } },
          yaxis: { lines: { show: false } },
        },
        legend: {
          show: false // We use custom legend or color differentiation
        },
        dataLabels: {
          enabled: false // Disable dataLabels to prevent formatter errors; rely on tooltip
        },
        tooltip: {
          theme: 'dark',
          y: {
            title: { formatter: () => "" },
            formatter: (val, opts) => {
              if (!opts || opts.seriesIndex === undefined || opts.dataPointIndex === undefined || !opts.w) {
                return `€${Number(val).toLocaleString()}`;
              }
              const pt = opts.w.config.series[opts.seriesIndex].data[opts.dataPointIndex];
              if (!pt || !pt.y || !Array.isArray(pt.y)) {
                return `€${Number(val).toLocaleString()}`;
              }
              return `€${pt.y[0].toLocaleString()} - €${pt.y[1].toLocaleString()}`;
            }
          }
        }
      }
    };
  }, []);

  const tableColumns = [
    { key: "region", name: "Region", align: "left" },
    { key: "exampleMarkets", name: "Example Markets", align: "left" },
    { key: "web17Price", name: "WEB 17 Price", align: "center" },
    { key: "artissePrice", name: "Artisse Price", align: "center" },
    {
      key: "difference",
      name: "Difference",
      align: "center",
      render: (v, row) => (
        <span style={{ color: row.diffColor, fontWeight: 700 }}>{v}</span>
      ),
    },
  ];

  const tableRows = useMemo(() => {
    return mockPricingData.regions.map((region) => {
      const ourAvg = (region.our_product.min + region.our_product.max) / 2;
      const theirAvg = (region.competitor_product.min + region.competitor_product.max) / 2;
      const diff = ourAvg - theirAvg;
      const diffPerc = ((diff / theirAvg) * 100).toFixed(1);
      const diffColor = diff > 0 ? "#10B981" : (diff < 0 ? "#EF4444" : "rgba(255,255,255,0.7)");
      const diffText = diff > 0 ? `+${diffPerc}%` : `${diffPerc}%`;

      return {
        region: region.name,
        exampleMarkets: region.examples,
        web17Price: region.our_product.label,
        artissePrice: region.competitor_product.label,
        difference: diffText,
        diffColor,
      };
    });
  }, []);

  return (
    <DashboardLayout>
      <DashboardNavbar onDownloadReport={() => {}} />

      <VuiBox py={3}>
        <Grid container spacing={3}>
          
          {/* Top Row: Summary Cards */}
          <Grid item xs={12} md={6} lg={3}>
            <GlassCard sx={{ height: "100%" }}>
              <Box p={2.5} sx={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center" }}>
                <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.6)", letterSpacing: "0.05em", mb: 0.5, lineHeight: 1.2 }}>
                  Avg Price Premium
                </Typography>
                <Typography variant="h3" sx={{ color: "#10B981", fontWeight: 800, mb: 0.5 }}>
                  {mockPricingData.summary.averagePremium}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                  {mockPricingData.summary.premiumText}
                </Typography>
              </Box>
            </GlassCard>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <GlassCard sx={{ height: "100%" }}>
              <Box p={2.5} sx={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center" }}>
                <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.6)", letterSpacing: "0.05em", mb: 0.5, lineHeight: 1.2 }}>
                  WEB 17 Price Range
                </Typography>
                <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700, mb: 0.5 }}>
                  {mockPricingData.summary.web17Range}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                  Across Europe Tiers
                </Typography>
              </Box>
            </GlassCard>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <GlassCard sx={{ height: "100%" }}>
              <Box p={2.5} sx={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center" }}>
                <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.6)", letterSpacing: "0.05em", mb: 0.5, lineHeight: 1.2 }}>
                  Artisse Price Range
                </Typography>
                <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700, mb: 0.5 }}>
                  {mockPricingData.summary.artisseRange}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                  Across Europe Tiers
                </Typography>
              </Box>
            </GlassCard>
          </Grid>

          <Grid item xs={12} md={6} lg={3}>
            <GlassCard sx={{ height: "100%" }}>
              <Box p={2.5} sx={{ position: "relative", zIndex: 1, display: "flex", flexDirection: "column", alignItems: "flex-start", justifyContent: "center" }}>
                <Typography variant="overline" sx={{ color: "rgba(255,255,255,0.6)", letterSpacing: "0.05em", mb: 0.5, lineHeight: 1.2 }}>
                  Markets Tracked
                </Typography>
                <Typography variant="h4" sx={{ color: "#fff", fontWeight: 700, mb: 0.5 }}>
                  {mockPricingData.summary.marketsTracked}
                </Typography>
                <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
                  DRG, Tendered, Capped
                </Typography>
              </Box>
            </GlassCard>
          </Grid>

          {/* Middle Row: Map & Range Bar Chart */}
          <Grid item xs={12} lg={6}>
            <GlassCard title="European Regional Pricing Map">
              <Box mt={2} sx={{ height: 500, position: "relative" }}>
                <RegionalPricingMap regions={mockPricingData.regions} />
              </Box>
            </GlassCard>
          </Grid>

          <Grid item xs={12} lg={6}>
            <GlassCard title="Regional Pricing Ranges (EUR)">
              <Box mt={2} sx={{ height: 500 }}>
                <Chart
                  options={chartData.options}
                  series={chartData.series}
                  type="rangeBar"
                  height={480}
                />
              </Box>
            </GlassCard>
          </Grid>

          {/* Bottom Row: Data Table */}
          <Grid item xs={12}>
            <GlassCard title="Pricing Data Table">
              <PorterTable columns={tableColumns} rows={tableRows} />
            </GlassCard>
          </Grid>

        </Grid>
      </VuiBox>
    </DashboardLayout>
  );
}

export default PricingAnalyticsPage;
